import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { platforms } from '@app/configurations';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoggingService } from '../services/error-logging.service';
import { UtilitiesService } from '../services/utilities.service';

@Injectable()
export class AccountsAPI {
  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private utilitiesService: UtilitiesService,
  ) {}

  /* #region accounts requests */

  getAccounts(refiners = []): Observable<{ total: number; data: any[] }> {
    // construct API endpoint using refiners to fetch required accounts
    return this.http
      .get(`${environment.app.resources.dashboard}accounts?${this.utilitiesService.refinersToUrl(refiners)}`, {})
      .pipe(
        map((response: any) => {
          return {
            total: response.meta.pagination.total,
            data: response.data.map((item: any) => ({
              adspertId: item.adspert_account_id.toString(),
              accountId: item.account_id.toString(),
              customerId: item.customer_id.toString(),
              name: item.account_name,
              optimize: item.optimize,
              isDemo: item.is_demo,
              tags: {
                user: item.tags_user,
              },
              status,
              optimizationStart: item.optimization_start,
              disconnected: item.disconnected,
              connectedAt: item.connected_at,
              rank: item.attention_score,
              snoozed: item.snoozed,
              campaignsOptimized: item.campaign_count_optimized === null ? undefined : item.campaign_count_optimized,
              campaignsActive: item.campaign_count_active === null ? undefined : item.campaign_count_active,
              campaignsTotal: item.campaign_count_total === null ? undefined : item.campaign_count_total,
              platformAccess: item.platform_access_status,
              platform: platforms[item.platform],
              signupCompleted: item.signup_completed,
            })),
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch accounts', error, 'error');
          return throwError('Failed to fetch accounts');
        }),
      );
  }

  patchAccount(id: string, payload: any) {
    return this.http.patch(`${environment.app.resources.dashboard}accounts/${id}`, payload, {}).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to update accounts', error, 'error');
        return throwError('Failed to update accounts');
      }),
    );
  }

  sendMCCInvitation(id: string) {
    return this.http
      .post(`${environment.app.resources.dashboard}accounts/${id}/google-ads-manager-account-invitation`, {}, {})
      .pipe(
        map(() => true),
        catchError((error: any) => {
          this.logger.logError('Failed to update MCC Invitation', error, 'error');
          return throwError('Failed to update MCC Invitation');
        }),
      );
  }

  deleteAccount(id: string) {
    return this.http.delete(`${environment.app.resources.dashboard}accounts/${id}`, {}).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to delete accounts', error, 'error');
        return throwError('Failed to delete accounts');
      }),
    );
  }

  disconnectAccount(id: string) {
    return this.http.patch(`${environment.app.resources.dashboard}accounts/${id}`, { disconnected: true }, {}).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to disconnect accounts', error, 'error');
        return throwError('Failed to disconnect accounts');
      }),
    );
  }

  reactivateAccount(id: string) {
    return this.http.patch(`${environment.app.resources.dashboard}accounts/${id}`, { snoozed: false }, {}).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to disconnect accounts', error, 'error');
        return throwError('Failed to disconnect accounts');
      }),
    );
  }

  reconnectAccount(id: string) {
    return this.http.patch(`${environment.app.resources.dashboard}accounts/${id}`, { disconnected: false }, {}).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to reconnect accounts', error, 'error');
        return throwError('Failed to reconnect accounts');
      }),
    );
  }

  getAccountTags(refiners = [], exludeScope = true): Observable<{ pagination: any; data: any[] }> {
    refiners.push(['include', `adspert_account_id,account_id,account_name,tag_name${exludeScope ? '' : ',tag_scope'}`]);
    return this.http
      .get(`${environment.app.resources.dashboard}accounts/tags?${this.utilitiesService.refinersToUrl(refiners)}`, {})
      .pipe(
        map((response: any) => {
          return {
            pagination: response.meta.pagination,
            data: response.data.map((item: any) => ({
              adspertId: item.adspert_account_id.toString(),
              accountId: item.account_id.toString(),
              accountName: item.account_name,
              name: item.tag_name,
              scope: item.tag_scope,
            })),
          };
        }),
        catchError((error: any) => {
          this.logger.logError('Failed to fetch tags', error, 'error');
          return throwError('Failed to fetch tags');
        }),
      );
  }

  addTags(payload: any) {
    return this.http.put(`${environment.app.resources.dashboard}accounts/tags`, payload, {}).pipe(
      map((response: any) => {
        return response.data;
      }),
      catchError((error: any) => {
        this.logger.logError('Failed to add tags', error, 'error');
        return throwError('Failed to add tags');
      }),
    );
  }
  deleteAccountTags(body) {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body,
    };
    return this.http.delete(`${environment.app.resources.dashboard}accounts/tags`, options).pipe(
      map(() => true),
      catchError((error: any) => {
        this.logger.logError('Failed to delete tags', error, 'error');
        return throwError('Failed to delete tags');
      }),
    );
  }
}
